import { ManagementDialog } from "@/components/modal/ManagementDialog";
import { Button } from "@/components/ui/button";
import { BtnSimpleIcon } from "@/components/ui/button/btn-simple-icon";
import { Pen, Trash2 } from "lucide-react";
import { cn } from "@/utils/cn";
import type { GradeTabRow } from "./GradeShiftConfigModal";

/**
 * Grade 추가 / 이름수정 / 삭제 전용 모달 (인원=shift 요구 설정 제외).
 * TeamManagementModal 과 동일 디자인.
 * - 추가/이름수정은 상위의 이름 입력 대화상자에서 처리한다.
 * - 저장/조회 endpoint 는 GradeSettingContents 의 기존 흐름 그대로 사용.
 */
type GradeManagementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** 모달 하단 저장 — 초안을 본 상태에 반영 */
  onSave: () => void;
  /** total 포함 가능 — 내부에서 제외 */
  grades: GradeTabRow[];
  onAddGrade: () => void;
  onRenameGrade: (gradeId: string, currentLabel: string) => void;
  onDeleteGrade: (gradeId: string) => void;
  addGradeDisabled?: boolean;
};

export default function GradeManagementModal({
  isOpen,
  onClose,
  onSave,
  grades,
  onAddGrade,
  onRenameGrade,
  onDeleteGrade,
  addGradeDisabled = false,
}: GradeManagementModalProps) {
  if (!isOpen) return null;

  const items = grades.filter((g) => g.id !== "total");

  return (
    <ManagementDialog
      onClose={onClose}
      title="Grade 관리"
      widthClassName="w-[560px] max-w-[calc(100vw-32px)]"
      footer={
        <>
          <Button type="button" variant="outline" className="rounded-full" onClick={onClose}>닫기</Button>
          <Button type="button" className="rounded-full bg-sig text-white hover:bg-sig/90" onClick={onSave}>저장</Button>
        </>
      }
    >
      <div className="mb-4 flex items-center justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={addGradeDisabled}
          onClick={onAddGrade}
        >
          + Grade 추가
        </Button>
      </div>

      <div className="flex flex-col gap-2 pb-4">
        {items.length === 0 && (
          <div className="text-center text-sm py-3 text-muted-foreground">
            생성된 Grade가 없습니다.
          </div>
        )}
        {items.map((grade) => (
          <div
            key={grade.id}
            className={cn(
              "flex min-w-0 items-center gap-2 rounded-md border border-border bg-transparent px-3 py-2 group"
            )}
          >
            <div className="min-w-0 flex-1 flex flex-col">
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                <p
                  className="min-w-0 truncate py-1 pl-0 pr-2 text-left text-sm font-semibold"
                  title={grade.label}
                >
                  {grade.label}
                </p>
                <div className="shrink-0">
                  <button
                    type="button"
                    className="p-1.5 text-muted-foreground hover:text-foreground"
                    onClick={() => onRenameGrade(grade.id, grade.label)}
                    aria-label={`${grade.label} 이름 수정`}
                  >
                    <Pen className="size-4" />
                  </button>
                  <BtnSimpleIcon
                    tone="destructive"
                    onClick={() => onDeleteGrade(grade.id)}
                    aria-label={`${grade.label} 삭제`}
                    icon={<Trash2 className="size-4" />}
                  />
                </div>
              </div>
              <p className="text-xs mt-1 text-muted-foreground flex items-center gap-1">
                {grade.count > 0 ? `${grade.count}명` : "인원 없음"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </ManagementDialog>
  );
}
